/*
 * Mise à jour côté client de la barre "livraison/cadeau offert" (giftbar).
 * Le giftbar est rendu depuis un block du groupe de sections : lors d'un re-rendu du drawer via la
 * Section Rendering API, les blocks ne sont pas transmis, donc on ne le re-rend jamais côté serveur.
 * On le conserve dans le DOM et on recalcule ici sa progression à partir du total du panier.
 * Le format monétaire est recalibré sur data-money-sample (rendu serveur, correct par marché/devise).
 */
function cartDrawerParseMoneyPattern(sample) {
  // sample = mise en forme "money" de 123456 (soit 1234.56 dans la devise active)
  const fallback = { prefix: '', suffix: '', decimalSep: '.', thousandsSep: '' };
  if (!sample) return fallback;
  const match = sample.match(/\d[\d.,'\u00A0\u202F ]*\d/);
  if (!match) return fallback;
  const num = match[0];
  const idx = sample.indexOf(num);
  return {
    prefix: sample.slice(0, idx),
    suffix: sample.slice(idx + num.length),
    decimalSep: num.slice(-3, -2) || '.',
    thousandsSep: (num.slice(0, -3).match(/\d(\D)\d/) || [])[1] || '',
  };
}

function cartDrawerFormatMoney(cents, pattern) {
  const negative = cents < 0;
  const value = (Math.abs(Math.round(cents)) / 100).toFixed(2);
  let [intPart, decPart] = value.split('.');
  if (pattern.thousandsSep) {
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, pattern.thousandsSep);
  }
  return (negative ? '-' : '') + pattern.prefix + intPart + pattern.decimalSep + decPart + pattern.suffix;
}

// Circonférence de l'anneau (r = 16) : doit correspondre à stroke-dasharray dans shipping_bar.liquid.
const CART_DRAWER_RING_LENGTH = 100.53;

function cartDrawerUpdateGiftbar(giftbar, cart) {
  const freeShip = parseInt(giftbar.dataset.freeShip, 10);
  if (!freeShip) return;

  // Même logique que shipping_bar.liquid : un seul objectif affiché (le prochain palier),
  // l'anneau repart de zéro entre le palier relais et le palier domicile.
  const total = cart.total_price;
  const homeAmount = giftbar.dataset.gift ? parseInt(giftbar.dataset.gift, 10) : null;
  const pattern = cartDrawerParseMoneyPattern(giftbar.dataset.moneySample);
  const fill = (tmpl, cents) => (tmpl || '').replace('__PRICE__', cartDrawerFormatMoney(cents, pattern));
  const data = giftbar.dataset;

  let mainHTML;
  let subText;
  let progress;
  if (homeAmount) {
    if (total < freeShip) {
      mainHTML = fill(data.tmplRelayRemaining, freeShip - total);
      subText = data.txtHomeFrom;
      progress = total / freeShip;
    } else if (total < homeAmount) {
      mainHTML = fill(data.tmplHomeRemaining, homeAmount - total);
      subText = data.txtRelayReached;
      progress = (total - freeShip) / Math.max(homeAmount - freeShip, 1);
    } else {
      mainHTML = data.txtHomeReached;
      subText = data.txtAllReached;
      progress = 1;
    }
  } else if (total < freeShip) {
    mainHTML = fill(data.tmplSingleRemaining, freeShip - total);
    subText = data.txtSingleFrom;
    progress = total / freeShip;
  } else {
    mainHTML = data.txtSingleReached;
    subText = '';
    progress = 1;
  }
  progress = Math.min(Math.max(progress, 0), 1);

  const ring = giftbar.querySelector('[data-giftbar-ring]');
  if (ring) ring.setAttribute('stroke-dashoffset', (CART_DRAWER_RING_LENGTH * (1 - progress)).toFixed(2));

  const main = giftbar.querySelector('[data-giftbar-main]');
  if (main) main.innerHTML = mainHTML || '';

  const sub = giftbar.querySelector('[data-giftbar-sub]');
  if (sub) sub.textContent = subText || '';

  const sep = giftbar.querySelector('[data-giftbar-sep]');
  if (sep) sep.hidden = !subText;
}

function refreshCartDrawerGiftbar() {
  const giftbar = document.querySelector('cart-drawer .drawer__giftbar[data-free-ship]');
  if (!giftbar) return;
  fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((response) => response.json())
    .then((cart) => cartDrawerUpdateGiftbar(giftbar, cart))
    .catch(() => {});
}
window.refreshCartDrawerGiftbar = refreshCartDrawerGiftbar;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
		this.addEventListener('click', (evt) => {
			if (evt.target && evt.target.id === 'CartDrawer-Overlay') {
				this.close();
			}
		});

		this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // SEO : images de l'upsell en data-src base64 (product-card-mini), chargées seulement à l'ouverture
    this.querySelectorAll('img[data-src]').forEach((img) => {
      img.src = atob(img.getAttribute('data-src'));
      img.removeAttribute('data-src');
    });
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      {once: true}
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Panier vide -> non vide : retirer is-empty de TOUS les conteneurs, sinon le CSS masque le
    // contenu (cart-drawer-items.is-empty + .drawer__footer ; cart-drawer.is-empty .drawer__header ;
    // #cart-drawer .is-empty .drawer__giftbar / .product-recommendations__wrapper).
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    const drawerItems = this.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.remove('is-empty');

    // On ne re-rend que l'en-tête, la liste d'articles et les totaux : le giftbar et l'upsell sont
    // rendus depuis les blocks du groupe de sections, indisponibles lors du re-rendu via la Section
    // Rendering API. On les laisse donc en place (jamais écrasés) et on met à jour le giftbar en JS.
    // Rendu défensif : on ignore une cible ou une source manquante plutôt que d'interrompre tout.
    this.getSectionsToRender().forEach((section) => {
      const sectionElement =
        (document.getElementById(section.id) && document.getElementById(section.id).querySelector(section.selector)) ||
        document.getElementById(section.id);
      const sectionHTML = parsedState.sections[section.section];
      if (!sectionElement || !sectionHTML) return;
      const source = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector(section.selector);
      if (!source) return;
      sectionElement.innerHTML = source.innerHTML;
    });

    // Transition panier vide -> non vide : retirer l'état vide résiduel s'il existe.
    const emptyBlock = this.querySelector('.drawer__inner-empty');
    if (emptyBlock) emptyBlock.remove();

    if (typeof window.refreshCartDrawerGiftbar === 'function') window.refreshCartDrawerGiftbar();

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    // On cible des sous-parties du drawer plutôt que tout #CartDrawer, pour ne PAS écraser
    // .drawer__giftbar et .product-recommendations__wrapper (rendus depuis les blocks du groupe,
    // absents du re-rendu isolé de la section).
    return [
      {
        id: 'cart-drawer',
        section: this.dataset.sectionId,
        selector: '.drawer__header',
      },
      {
        id: 'cart-drawer',
        section: this.dataset.sectionId,
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'cart-drawer',
        section: this.dataset.sectionId,
        selector: '.cart-drawer__footer',
      },
      {
        // .cart__ctas est FRÈRE de .cart-drawer__footer (pas enfant) : sans cette cible, le bouton
        // #CartDrawer-Checkout garde le `disabled` rendu par Liquid quand la page a été chargée
        // panier vide, et reste inactif après le premier ajout.
        id: 'cart-drawer',
        section: this.dataset.sectionId,
        selector: '.cart__ctas',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // Mêmes cibles ciblées que CartDrawer (voir commentaire ci-dessus) pour préserver giftbar/upsell
    // lors des changements/suppressions de quantité dans le drawer.
    return [
      {
        id: 'cart-drawer',
        section: this.dataset.sectionId,
        selector: '.drawer__header',
      },
      {
        id: 'cart-drawer',
        section: this.dataset.sectionId,
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'cart-drawer',
        section: this.dataset.sectionId,
        selector: '.cart-drawer__footer',
      },
      {
        // Voir CartDrawer.getSectionsToRender : cible indispensable pour l'état `disabled` du bouton
        // de paiement (rétabli aussi quand le panier redevient vide).
        id: 'cart-drawer',
        section: this.dataset.sectionId,
        selector: '.cart__ctas',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
